import _extends from "@babel/runtime/helpers/extends";
/*
 *                     NOTICE
 *            COPYRIGHT 2022 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import * as AbReactResources from "abreact/generated/locale/AbReactResources";
import { AbPopup } from "abreact/incubator/ui/popup/AbPopup";
import { PopupPosition } from "abreact/incubator/ui/popup/PopupPosition";
import { PopupUtils } from "abreact/incubator/ui/popup/PopupUtils";
import { ReactUtils } from "abreact/incubator/ui/utils/ReactUtils";
import { AbTextInput } from "abreact/input/AbTextInput";
import { AbDateTimeInputPopupButton } from "abreact/input/date/AbDateTimeInputPopupButton";
import { AbIcon } from "abreact/misc/AbIcon";
import { AbSizeContext, sizeProviderWrapper } from "abreact/misc/AbSize";
import * as AbHooks from "abreact/utils/AbHooks";
import * as React from "react";
export function AbDateTimeCommonInput(props) {
  const {
    textIsValid,
    inRange,
    inputString,
    textOnChange,
    onCommit
  } = AbHooks.useStringValidation({
    value: props.value,
    invalidString: props.invalidString,
    onChange: props.onChange,
    inRange: props.inRange,
    format: props.format,
    equals: props.equals
  });
  const textInputOnChange = v => {
    props.textInputProps?.onChange?.(v); // Order of these two calls matters
    textOnChange(v);
  };
  const partialRef = React.useRef({});
  partialRef.current.clear = () => textInputOnChange("");
  const isRefReady = partial => !!partial.clear && !!partial.inputElement;
  const inputRef = AbHooks.useComposeRef(props, partialRef.current, "inputElement", isRefReady);
  const format = value => value ? props.format.format(value) : "";
  const defaultInputId = AbHooks.useUniqueId();
  const contextSize = React.useContext(AbSizeContext);
  const [textInputFocused, setTextInputFocused] = React.useState(false);
  const errorString = props.getErrorString(textIsValid, inRange);
  const examplePositioner = React.useCallback(props => {
    const padding = partialRef.current.inputElement ? parseInt(getComputedStyle(partialRef.current.inputElement).paddingLeft) : 12;
    const partialPadding = padding * 0.75;
    const left = PopupUtils.positionLeft(props, props.relativeToClientRect.left + partialPadding, props.relativeToClientRect.right - partialPadding - props.popupElement.clientWidth, false).left;
    return {
      popupPosition: PopupPosition.ABOVE,
      top: props.relativeToClientRect.top - 5,
      left: left
    };
  }, []);
  const textInputStyle = partialRef.current.inputElement ? getComputedStyle(partialRef.current.inputElement) : undefined;
  const textInputPadding = textInputStyle ? parseInt(textInputStyle.paddingLeft) : 12;
  const inner = /*#__PURE__*/React.createElement("div", ReactUtils.componentProps("ab-date-time-common-input", props, [props.disabled && "ab-disabled", props.size ?? contextSize, (!textIsValid || !inRange) && "ab-date-time-error", props.buttonProps && "ab-date-time-with-button", "ab-font-feature-tabular-numbers"]), textInputFocused && /*#__PURE__*/React.createElement(AbPopup, {
    className: "ab-date-time-example-popup",
    modal: false,
    onBackgroundClick: () => true // Makes it transparent to clicks
    ,
    onBackgroundCaptureMouseDown: () => true // Makes it transparent to clicks
    ,
    style: {
      "--ab-size-default-padding": `${textInputPadding}px`
    },
    onClose: () => setTextInputFocused(false),
    origin: partialRef.current?.inputElement ?? undefined,
    positioner: examplePositioner,
    flushContent: true
  }, /*#__PURE__*/React.createElement("div", {
    className: "ab-date-time-example"
  }, AbReactResources.datePicker_example(format(props.format.example)))), /*#__PURE__*/React.createElement(AbTextInput, {
    value: inputString,
    onChange: textInputOnChange,
    id: props.id ? `${props.id}-input` : defaultInputId,
    onEnter: () => onCommit(inputString),
    onBlur: () => {
      setTextInputFocused(false);
      onCommit(inputString, true);
    },
    onFocus: () => setTextInputFocused(true),
    name: props.name,
    placeholder: props.placeholder,
    disabled: props.disabled,
    tabIndex: props.tabIndex,
    autoFocus: props.autoFocus,
    tooltip: props.tooltip,
    abRef: value => inputRef(value?.inputElement ?? null)
  }), (!textIsValid || !inRange) && /*#__PURE__*/React.createElement(AbIcon, {
    image: "absdk:error",
    className: "ab-date-time-error-icon",
    tooltip: errorString
  }), props.buttonProps && /*#__PURE__*/React.createElement(AbDateTimeInputPopupButton, _extends({}, props.buttonProps, {
    disabled: props.disabled,
    onChange: val => {
      onCommit(val && props.buttonProps ? props.buttonProps?.format(val) : "", true);
    },
    textInputRef: {
      current: isRefReady(partialRef.current) ? partialRef.current : null
    }
  })));
  return sizeProviderWrapper(inner, props, contextSize);
}
//# sourceMappingURL=AbDateTimeCommonInput.js.map
